'use client';

import { cn } from '@/lib/utils';
import { Progress } from '@/components/ui/progress';
import type { RunwayStatus } from '@/lib/types';

interface HealthBarProps {
  runwaySeconds: number;
  maxDays?: number;
  showLabel?: boolean;
  className?: string;
}

export function HealthBar({
  runwaySeconds,
  maxDays = 90,
  showLabel = true,
  className,
}: HealthBarProps) {
  const days = runwaySeconds / 86400;
  const progress = Math.min((days / maxDays) * 100, 100);

  // Determine status — warning only shows at 4 days or less
  let status: RunwayStatus = 'healthy';
  if (days <= 1) status = 'danger';
  else if (days <= 2) status = 'critical';
  else if (days <= 4) status = 'warning';

  const statusColors: Record<RunwayStatus, string> = {
    healthy: 'bg-win-green',
    warning: 'bg-yellow-500',
    critical: 'bg-orange-500',
    danger: 'bg-loss-red',
  };

  const formatLabel = () => {
    if (days < 1) {
      const hours = Math.floor(runwaySeconds / 3600);
      return hours <= 0 ? 'Forfeiting!' : `${hours}h left`;
    }
    return `${Math.floor(days)} days left`;
  };

  return (
    <div className={cn('space-y-1', className)}>
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-gray-800">
        <div
          className={cn(
            'h-full transition-all duration-500',
            statusColors[status],
            status === 'danger' && 'animate-pulse'
          )}
          style={{ width: `${progress}%` }}
        />
      </div>
      {showLabel && (
        <p
          className={cn(
            'text-xs',
            status === 'healthy' && 'text-gray-400',
            status === 'warning' && 'text-yellow-500',
            status === 'critical' && 'text-orange-500',
            status === 'danger' && 'text-loss-red font-medium'
          )}
        >
          {formatLabel()}
        </p>
      )}
    </div>
  );
}
